import { SkeletonLoader, TooltipTrigger } from '@design';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { config } from '@terminal/config/app.config';
import { useCatalystInsightsList } from '@terminal/react-query-hooks/insights/useCatalystInsights';
import { useEarningsInsightsFilter } from '@terminal/react-query-hooks/insights/useEarningsInsightsFilter';
import { parseEarningsInsights } from '@terminal/react-query-hooks/insights/useEarningsInsightsFilter.utils';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { CompanyCatalystCounterTooltip } from '@terminal/views/dashboard/my-assets-widget/assets-table/insights-cell/company-catalyst-counter-tooltip/CompanyCatalystCounterTooltip';
import { CatalystInsightIcon } from '@terminal/widgets/catalyst-insights-drawer/catalyst-insight-icon/CatalystInsightIcon';
import { EarningsInsightIcon } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-icon/EarningsInsightIcon';
import { startOfDay, subWeeks } from 'date-fns';
import React, { useMemo, useState } from 'react';

import styles from './CompetitorsSection.module.scss';

const MAX_COMPANY_CATALYSTS = 2;

export interface CompetitorCatalystsCellProps {
  entityTag: string;
  source: PosthogTrackingSource;
}

export const CompetitorCatalystsCell = ({
  entityTag,
  source,
}: CompetitorCatalystsCellProps) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );
  const canShowCompanyCatalysts = !isBasicOrLightUser;

  const fromDate = useMemo(() => startOfDay(subWeeks(new Date(), 1)), []);

  const { data: catalystInsights = [], isFetching: isCatalystsFetching } =
    useCatalystInsightsList(
      {
        entities: [entityTag],
        categories: ['company'],
        from: fromDate.toISOString(),
      },
      canShowCompanyCatalysts
    );

  const { data: earningsInsightsData = [], isFetching: isEarningsFetching } =
    useEarningsInsightsFilter({
      body: { entities: [entityTag], from: fromDate.toISOString() },
      isEnabled: !isBasicOrLightUser,
    });

  const earningsInsight = useMemo(
    () => parseEarningsInsights(earningsInsightsData),
    [earningsInsightsData]
  );
  const isLoading = isEarningsFetching || isCatalystsFetching;

  if (isLoading) {
    return (
      <SkeletonLoader
        className={styles.styledSkeletonLoaderWrapper}
        areas={[
          { styles: { height: '20px', width: '100%', borderRadius: '8px' } },
        ]}
      />
    );
  }

  const exceedsMaxCatalysts = catalystInsights.length > MAX_COMPANY_CATALYSTS;
  const visibleCatalystInsights = catalystInsights.slice(
    0,
    MAX_COMPANY_CATALYSTS
  );

  const remainingCatalystInsights = exceedsMaxCatalysts
    ? catalystInsights.slice(MAX_COMPANY_CATALYSTS)
    : [];

  return (
    <div className={styles.insightsContainer}>
      {earningsInsight ? (
        <EarningsInsightIcon
          entityTag={entityTag}
          earningsInsight={earningsInsight}
          width={18}
          height={20}
          source={source}
          isClickable={!config.app.isTeamsApp}
        />
      ) : (
        <div className={styles.placeholder} />
      )}
      {visibleCatalystInsights.map(catalystInsight => (
        <CatalystInsightIcon
          key={catalystInsight.id}
          catalystInsightId={catalystInsight.id}
          subCategory={catalystInsight.sub_category}
          isBordered
          customSize={{ width: 18, height: 20 }}
          identifier={entityTag}
          source={source}
          category={catalystInsight.category}
          isClickable={!config.app.isTeamsApp}
        />
      ))}
      {!!remainingCatalystInsights.length && (
        <TooltipContainer
          label={
            <CompanyCatalystCounterTooltip
              insights={remainingCatalystInsights}
              source={source}
              onClose={() => setIsTooltipOpen(false)}
            />
          }
          inPortal
          trigger={[TooltipTrigger.Hover]}
          placement="top-end"
          withSafePolygon
          isOpen={isTooltipOpen}
          onOpen={() => setIsTooltipOpen(true)}
          onClose={() => setIsTooltipOpen(false)}
        >
          <div className={styles.insightCount}>
            +{remainingCatalystInsights.length}
          </div>
        </TooltipContainer>
      )}
    </div>
  );
};
